import React, { ChangeEvent, useState } from 'react';
import { RootState } from 'redux/store';
import { setCraApiVariable } from 'redux/actions/CraApiVariableActions';
import { useSelector, useDispatch } from 'react-redux';
import MessageUtil from 'util/MessageUtil';
import { ApiVariableParameter } from 'domain/enrichment/EnrichmentDomain';
import FormicaInput from 'components/custom/FormicaInput';
import RoleHelper from 'RoleHelper';
import { AnimatedButton } from '@trlogic/ui-pack';
import ComponentName from 'constant/ComponentName';
import LanguageUtil from 'util/LanguageUtil';
import { ColumnProps } from 'primereact/column';
import { customConfirm } from 'components/custom/CustomPopupConfirm';
import CustomInput from 'components/custom/CustomInput';
import CustomTable from 'components/custom/CustomTable';
import Icon from 'icons/Icon';
import EmptyListComponent from 'components/custom/empty-components/EmptyListComponent';
import { CraApiVariableSchema } from 'domain/cra/CraDomains';

export default function ParametersComponent({ name }: { name: string }) {
  const { craApiVariables } = useSelector((state: RootState) => state.craApiVariable);
  const dispatch = useDispatch();
  const currentVariable = craApiVariables[name];
  const [parameter, setParameter] = useState<ApiVariableParameter>({
    key: '',
    value: '',
    description: '',
    isActive: true,
  });

  if (!currentVariable) {
    return null;
  }

  const updateUrlFromParameters = (variable: CraApiVariableSchema) => {
    const baseUrl = variable.url.split('?')[0];
    const activeParams = variable?.parameters?.filter((p) => p.isActive !== false && p.key && p.value);
    if (activeParams && activeParams.length > 0) {
      variable.url = baseUrl + '?' + activeParams.map((p) => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
    } else {
      variable.url = baseUrl;
    }
  };

  const handleSubmit = () => {
    if (currentVariable?.parameters?.find((p) => p.key === parameter.key)) {
      MessageUtil.warning({
        summary: 'Parameter with this key already exists',
        detail: <div>can't saved.</div>,
      });
      return false;
    }
    if (parameter.key && parameter.value && parameter.key.trim().length > 0 && parameter.value.trim().length > 0) {
      const updated = { ...currentVariable, parameters: [...(currentVariable.parameters || []), parameter] };
      updateUrlFromParameters(updated);
      dispatch(setCraApiVariable(name, updated));
      setParameter({ ...parameter, key: '', value: '', description: '' });
    } else {
      MessageUtil.error({
        summary: 'Value and key are required',
      });
    }
  };

  const onChangeActive = (e: ChangeEvent<HTMLInputElement>, variableParameter: ApiVariableParameter) => {
    variableParameter.isActive = e.target.checked;
    const updated = { ...currentVariable };
    updateUrlFromParameters(updated);
    dispatch(setCraApiVariable(name, updated));
  };

  const onChangeKey = (e: ChangeEvent<HTMLInputElement>, variableParameter: ApiVariableParameter) => {
    variableParameter.key = e.currentTarget.value;
    const updated = { ...currentVariable };
    updateUrlFromParameters(updated);
    dispatch(setCraApiVariable(name, updated));
  };

  const onChangeValue = (e: ChangeEvent<HTMLInputElement>, variableParameter: ApiVariableParameter) => {
    variableParameter.value = e.currentTarget.value;
    const updated = { ...currentVariable };
    updateUrlFromParameters(updated);
    dispatch(setCraApiVariable(name, updated));
  };

  const onChangeDescription = (e: ChangeEvent<HTMLInputElement>, variableParameter: ApiVariableParameter) => {
    variableParameter.description = e.currentTarget.value;
    const updated = { ...currentVariable };
    dispatch(setCraApiVariable(name, updated));
  };

  const deleteApiVariableParameter = (parameter: ApiVariableParameter) => {
    const updated = { ...currentVariable, parameters: currentVariable?.parameters?.filter((p: ApiVariableParameter) => p.key != parameter.key) };
    updateUrlFromParameters(updated);
    dispatch(setCraApiVariable(name, updated));
  };

  const getColumns = (): ColumnProps[] => {
    return [
      {
        field: 'isActive',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.parameters.table.active', defaultMessage: 'Active' }),
        headerClassName: 'h-[44px] 2xl:h-[52px]',
        className: 'w-[7%]',
        body: (variableParameter: ApiVariableParameter) => (
          <div className={'flex items-center justify-center'}>
            <input
              type={'checkbox'}
              className={'accent-f-purple text-white w-4 h-4 border-[1px] border-[#EAECF0] bg-white'}
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeActive(e, variableParameter)}
              checked={variableParameter.isActive}
              data-testid={`${ComponentName.API_VARIABLE}_parameter_active_checkbox_${variableParameter.key}`}
            />
          </div>
        ),
      },
      {
        field: 'key',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.parameters.table.key', defaultMessage: 'Key' }),
        className: 'w-1/4',
        body: (variableParameter: ApiVariableParameter) => (
          <CustomInput
            inputType={'ghost'}
            type={'text'}
            disabled={!RoleHelper.canEdit('EnrichmentApi')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeKey(e, variableParameter)}
            value={variableParameter.key}
            data-testid={`${ComponentName.API_VARIABLE}_parameter_key_input_${variableParameter.key}`}
          />
        ),
      },
      {
        field: 'value',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.parameters.table.value', defaultMessage: 'Value' }),
        className: 'w-1/4',
        body: (variableParameter: ApiVariableParameter) => (
          <CustomInput
            inputType={'ghost'}
            type={'text'}
            disabled={!RoleHelper.canEdit('EnrichmentApi')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeValue(e, variableParameter)}
            value={variableParameter.value}
            data-testid={`${ComponentName.API_VARIABLE}_parameter_value_input_${variableParameter.key}`}
          />
        ),
      },
      {
        field: 'description',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.parameters.table.description', defaultMessage: 'Description' }),
        className: 'w-1/3',
        body: (variableParameter: ApiVariableParameter) => (
          <CustomInput
            inputType={'ghost'}
            type={'text'}
            disabled={!RoleHelper.canEdit('EnrichmentApi')}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeDescription(e, variableParameter)}
            value={variableParameter.description}
            data-testid={`${ComponentName.API_VARIABLE}_parameter_description_input_${variableParameter.key}`}
          />
        ),
      },
      {
        className: 'w-[5%]',
        alignHeader: 'center',
        header: LanguageUtil.get({ id: 'pages.enrichment.apiEnrichment.parameters.table.action', defaultMessage: 'action' }),
        sortable: false,
        body: (variableParameter: ApiVariableParameter) => (
          <div
            className={'flex w-full items-center justify-center'}
            onClick={(e: any) => {
              e.stopPropagation();
              e.preventDefault();
              RoleHelper.canEdit('EnrichmentApi') &&
                customConfirm({
                  target: e.currentTarget,
                  accept: () => deleteApiVariableParameter(variableParameter),
                  message: LanguageUtil.get({ id: 'messages.confirm.removeVariable', defaultMessage: 'Are you sure remove this variable?' }),
                });
            }}
            data-testid={`${ComponentName.API_VARIABLE}_parameter_remove_button_${variableParameter.key}`}
          >
            <Icon
              icon={'delete'}
              size={20}
              color={'primary'}
              className={'hover:text-primary text-[#607080]  hover:cursor-pointer flex items-center justify-center'}
            />
          </div>
        ),
      },
    ];
  };

  return (
    <div className={'flex flex-col w-full h-full mt-5'}>
      <div>
        <div className={'flex w-full gap-x-4 items-center mb-5 justify-start px-1'}>
          <div className={'w-1/3'}>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e) => setParameter({ ...parameter, key: e.currentTarget.value })}
              value={parameter.key}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.key', defaultMessage: 'Key' })}
              data-testid={`${ComponentName.API_VARIABLE}_parameter_key_creator_input`}
            />
          </div>
          <div className={'w-1/3'}>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e) => setParameter({ ...parameter, value: e.currentTarget.value })}
              value={parameter.value}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.value', defaultMessage: 'Value' })}
              data-testid={`${ComponentName.API_VARIABLE}_parameter_value_creator_input`}
            />
          </div>
          <div className={'w-1/3'}>
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onChange={(e) => setParameter({ ...parameter, description: e.currentTarget.value })}
              value={parameter.description}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
              data-testid={`${ComponentName.API_VARIABLE}_parameter_description_creator_input`}
            />
          </div>
          <div>
            <AnimatedButton
              disabled={!RoleHelper.canEdit('EnrichmentApi')}
              onClick={handleSubmit}
              customSize={'sm'}
              icon={'add'}
              customType={'primary'}
              text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
              data-testid={`${ComponentName.API_VARIABLE}_parameter_key_add_button`}
            />
          </div>
        </div>
      </div>
      <div className='h-full pb-5'>
        {currentVariable.parameters?.length === 0 ? (
          <EmptyListComponent />
        ) : (
          <div className={'!max-h-[calc(100vh-30rem)] overflow-y-scroll'}>
            <CustomTable columns={getColumns()} value={currentVariable.parameters} />
          </div>
        )}
      </div>
    </div>
  );
}
